import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categories, resources } from '@/mocks/data';
import { breadcrumbSchema } from '@/mocks/schema';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const filteredResources = activeCategory === 'all'
    ? resources
    : resources.filter((r) => r.category === activeCategory);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryTitle = currentCategory ? currentCategory.name : '全部资源';

  useEffect(() => {
    const scriptId = 'schema-breadcrumb';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();

    const schema = breadcrumbSchema([
      { name: '首页', url: 'https://91.n' },
      { name: '分类导航', url: 'https://91.n/category/all' },
      { name: categoryTitle, url: `https://91.n/category/${activeCategory}` },
    ]);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, [activeCategory, categoryTitle]);

  return (
    <>
      <SEOHead title={categoryTitle} description={`91.n 分类导航 - ${categoryTitle}，浏览优质网站资源，快速找到您需要的网络服务。`} />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-16 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '分类导航', path: '/category/all' },
            { label: categoryTitle },
          ]} />

          <div className="mt-4 mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-2">{categoryTitle}</h1>
            {currentCategory && (
              <p className="text-foreground-600 text-base">{currentCategory.description}</p>
            )}
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-3 mb-8 scrollbar-hide">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Resource Grid */}
          {filteredResources.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredResources.map((resource) => (
                <a
                  key={resource.id}
                  href={resource.url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="group bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-300 transition-all duration-200 flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-500 transition-colors duration-200">
                    <i className={`${resource.icon} text-lg text-primary-600 group-hover:text-background-50 transition-colors duration-200`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground-900 text-sm mb-1 group-hover:text-primary-500 transition-colors">{resource.title}</h3>
                    <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">{resource.description}</p>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {resource.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-background-100 text-foreground-500 text-xs rounded-full">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-foreground-400 group-hover:text-primary-500 transition-colors">
                    <i className="ri-external-link-line text-sm"></i>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无资源</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}